import { useState } from "react";
import { Gauge, KeyRound, Loader2, Plus, RotateCcw, Save, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/PageHeader";
import { AiModelEntryCard } from "@/components/ai/AiModelEntryCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAiSettings, useSaveAiSettings, useTestAiModel } from "@/hooks/use-ai";
import { describeCallableError, type AiSettingsView, type AiTestResult } from "@/lib/callables";
import {
  draftFromView,
  draftProblem,
  inheritSourceIndex,
  newEntryDraft,
  toModelDrafts,
  toSettingsInput,
  type AiEntryDraft,
  type AiSettingsDraft,
} from "@/lib/aiSettings";
import { appEnv, isProduction } from "@/lib/firebase";
import { formatDateTime, formatNumber } from "@/lib/format";

export default function AiSettings() {
  const settings = useAiSettings();
  const environment = isProduction ? "produção" : appEnv === "homologation" ? "homologação" : `o ambiente ${appEnv}`;

  return (
    <>
      {settings.data ? (
        // A tela remonta com o rascunho novo depois de salvar: a configuração
        // salva vira o ponto de partida, e o formulário não guarda chave digitada.
        <AiSettingsForm
          key={settings.data.updatedAt ?? "sem-configuracao"}
          view={settings.data}
          loadedAt={settings.dataUpdatedAt}
          environment={environment}
        />
      ) : (
        <>
          <PageHeader
            title="Configurações de IA"
            description={`Provedor, modelos, chave de API, instruções e limites de uso da IA em ${environment}.`}
          />
          {settings.isError ? (
            <Card>
              <CardContent>
                <EmptyState
                  icon={Sparkles}
                  title="Não foi possível carregar a configuração de IA"
                  description={describeCallableError(settings.error)}
                  action={
                    <Button variant="outline" onClick={() => void settings.refetch()}>
                      <RotateCcw className="size-4" />
                      Tentar de novo
                    </Button>
                  }
                />
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full max-w-md" />
              <Skeleton className="h-64 w-full" />
            </div>
          )}
        </>
      )}
    </>
  );
}

interface AiSettingsFormProps {
  view: AiSettingsView;
  loadedAt: number;
  environment: string;
}

function AiSettingsForm({ view, loadedAt, environment }: AiSettingsFormProps) {
  const save = useSaveAiSettings();
  const test = useTestAiModel();
  const [draft, setDraft] = useState<AiSettingsDraft>(() => draftFromView(view));
  // O que foi carregado, para saber se há o que salvar.
  const [initial] = useState(() => JSON.stringify(toSettingsInput(draftFromView(view))));
  const [testing, setTesting] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, AiTestResult>>({});

  const dirty = JSON.stringify(toSettingsInput(draft)) !== initial;
  const models = toModelDrafts(draft.entries);

  function updateEntry(index: number, patch: Partial<AiEntryDraft>) {
    setDraft((current) => ({
      ...current,
      entries: current.entries.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)),
    }));
    // O resultado do teste era do item como estava antes.
    const key = draft.entries[index]?.key;
    if (key && testResults[key]) {
      setTestResults((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
    }
  }

  function addEntry() {
    setDraft((current) => {
      const last = current.entries[current.entries.length - 1];
      const provider = view.providers.find((item) => item.id === last?.provider) ?? view.providers[0];
      const entry = newEntryDraft(provider);
      const entries = [...current.entries, entry];
      // A reserva nova nasce no provedor do item anterior, herdando a chave dele.
      entry.useOwnKey = inheritSourceIndex(entries, entries.length - 1) === null;
      return { ...current, entries };
    });
  }

  function removeEntry(index: number) {
    setDraft((current) => ({ ...current, entries: current.entries.filter((_, i) => i !== index) }));
  }

  function updateFeature(id: string, patch: Partial<AiSettingsDraft["features"][string]>) {
    setDraft((current) => ({
      ...current,
      features: { ...current.features, [id]: { ...current.features[id], ...patch } },
    }));
  }

  async function runTest(index: number) {
    const entry = draft.entries[index];
    setTesting(entry.key);
    try {
      const result = await test.mutateAsync({ models, index });
      setTestResults((current) => ({ ...current, [entry.key]: result }));
    } catch (error) {
      toast.error(describeCallableError(error));
    } finally {
      setTesting(null);
    }
  }

  async function submit() {
    const problem = draftProblem(draft, view);
    if (problem) {
      toast.error(problem);
      return;
    }
    try {
      await save.mutateAsync(toSettingsInput(draft));
      toast.success("Configurações de IA salvas.");
    } catch (error) {
      toast.error(describeCallableError(error));
    }
  }

  return (
    <>
      <PageHeader
        title="Configurações de IA"
        description={`Provedor, modelos, chave de API, instruções e limites de uso da IA em ${environment}. Cada ambiente tem a própria configuração.`}
        actions={
          <Button onClick={() => void submit()} disabled={!dirty || save.isPending}>
            {save.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            Salvar
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {view.configured ? (
          <Badge variant="ok" dot>
            IA configurada
          </Badge>
        ) : (
          <Badge variant="warning" dot>
            IA sem modelo com chave: as funcionalidades estão indisponíveis
          </Badge>
        )}
        {view.updatedAt ? (
          <span className="text-caption text-muted-foreground">
            Salva em {formatDateTime(view.updatedAt)}
          </span>
        ) : null}
        {dirty ? <Badge variant="muted">Alterações não salvas</Badge> : null}
      </div>

      <Tabs defaultValue="provedor">
        <TabsList>
          <TabsTrigger value="provedor">
            <KeyRound className="size-4" />
            Provedor e credenciais
          </TabsTrigger>
          <TabsTrigger value="instrucoes">
            <Sparkles className="size-4" />
            Funcionalidades e instruções
          </TabsTrigger>
          <TabsTrigger value="limites">
            <Gauge className="size-4" />
            Limites de uso
          </TabsTrigger>
        </TabsList>

        <TabsContent value="provedor">
          <Card>
            <CardHeader>
              <CardTitle>Provedor e credenciais</CardTitle>
              <CardDescription>
                O modelo principal atende todas as funcionalidades de IA. Quando ele atinge o limite
                de uso ou falha, a plataforma tenta o próximo da lista sozinha. O modelo que bateu no
                limite sai da frente por alguns minutos, e as próximas chamadas já começam pelo
                seguinte. A chave fica no servidor e não volta para esta tela.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {draft.entries.map((entry, index) => (
                <AiModelEntryCard
                  key={entry.key}
                  entries={draft.entries}
                  index={index}
                  providers={view.providers}
                  models={models}
                  now={loadedAt}
                  onChange={(patch) => updateEntry(index, patch)}
                  onRemove={draft.entries.length > 1 ? () => removeEntry(index) : undefined}
                  onTest={() => void runTest(index)}
                  isTesting={testing === entry.key}
                  testResult={testResults[entry.key] ?? null}
                />
              ))}
              {draft.entries.length < view.maxModels ? (
                <Button type="button" variant="outline" onClick={addEntry}>
                  <Plus className="size-4" />
                  Adicionar modelo de reserva
                </Button>
              ) : (
                <p className="text-caption text-muted-foreground">
                  A fila chegou ao máximo de {view.maxModels} modelos.
                </p>
              )}
              <p className="text-caption text-muted-foreground">
                O teste usa o que está na tela, inclusive a chave digitada e ainda não salva.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="instrucoes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Instruções gerais</CardTitle>
              <CardDescription>
                Valem para todas as funcionalidades de IA, junto com as regras fixas de cada uma. Use
                para o que é da plataforma inteira, como o tom e o idioma.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-1.5">
              <Label htmlFor="ia-instrucoes-gerais">Instruções</Label>
              <Textarea
                id="ia-instrucoes-gerais"
                value={draft.generalInstructions}
                onChange={(event) => setDraft((current) => ({ ...current, generalInstructions: event.target.value }))}
                maxLength={view.instructionsMax}
                rows={4}
                placeholder="Ex.: os estabelecimentos são padarias brasileiras; escreva em português do Brasil."
              />
              <p className="text-right text-caption text-muted-foreground">
                {draft.generalInstructions.length} de {formatNumber(view.instructionsMax)}
              </p>
            </CardContent>
          </Card>

          {view.features.map((feature) => {
            const value = draft.features[feature.id];
            return (
              <Card key={feature.id}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle>{feature.label}</CardTitle>
                      <CardDescription>{feature.description}</CardDescription>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Label htmlFor={`ia-${feature.id}-ligada`} className="text-caption">
                        {value.enabled ? "Ligada" : "Desligada"}
                      </Label>
                      <Switch
                        id={`ia-${feature.id}-ligada`}
                        checked={value.enabled}
                        onCheckedChange={(enabled) => updateFeature(feature.id, { enabled })}
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-1.5">
                  <Label htmlFor={`ia-${feature.id}-instrucoes`}>Instruções desta funcionalidade</Label>
                  <Textarea
                    id={`ia-${feature.id}-instrucoes`}
                    value={value.instructions}
                    onChange={(event) => updateFeature(feature.id, { instructions: event.target.value })}
                    maxLength={view.instructionsMax}
                    rows={4}
                    placeholder={feature.instructionsPlaceholder}
                  />
                  <p className="text-right text-caption text-muted-foreground">
                    {value.instructions.length} de {formatNumber(view.instructionsMax)}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="limites" className="space-y-4">
          {view.features.map((feature) => {
            const value = draft.features[feature.id];
            return (
              <Card key={feature.id}>
                <CardHeader>
                  <CardTitle>{feature.label}</CardTitle>
                  <CardDescription>
                    Quantas vezes por dia cada estabelecimento pode usar. O dia é o de São Paulo, e a
                    leitura que falha inteira não conta. A chamada à IA é paga pela plataforma.
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor={`ia-${feature.id}-limite`}>Limite por estabelecimento por dia</Label>
                    <Input
                      id={`ia-${feature.id}-limite`}
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={feature.maxDailyLimit}
                      value={value.dailyLimit}
                      onChange={(event) => updateFeature(feature.id, { dailyLimit: event.target.value })}
                    />
                    <p className="text-caption text-muted-foreground">
                      De 1 a {feature.maxDailyLimit}. Padrão: {feature.defaultDailyLimit}.
                    </p>
                  </div>
                  <div className="rounded-md border border-border p-3">
                    <p className="text-caption text-muted-foreground">Uso de hoje</p>
                    <p className="text-h5 font-black">
                      {formatNumber(feature.usageToday.reads)}{" "}
                      {feature.usageToday.reads === 1 ? "leitura" : "leituras"}
                    </p>
                    <p className="text-caption text-muted-foreground">
                      em {formatNumber(feature.usageToday.sellers)}{" "}
                      {feature.usageToday.sellers === 1 ? "estabelecimento" : "estabelecimentos"}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>
    </>
  );
}
